import React, { useState, useEffect } from 'react';
import { Add, Edit, Delete } from '@mui/icons-material';
import { useTranslation } from 'core/contexts/localization';
import { useUnits } from 'core/contexts/units';
import { useAuth } from 'core/contexts/auth';
import { toast } from 'react-toastify';
import { BasicTable, AlertDialog as Dialog, CircularIndeterminate, Chip, CheckboxComponent as Checkbox } from '@utriper/core-ui';
import { Box, Typography, Button, IconButton, Tooltip, Alert, TextField } from '@mui/material';
import Layout from 'components/Layout';
import UnitForm from './components/UnitForm';
import { useStyles } from './styles';

export const UnitsPage = () => {
    const { t: translate } = useTranslation();
    const classes = useStyles();
    const { userInfo } = useAuth();
    const { loading, units, createUnit, getUnits, updateUnit, deleteUnit } = useUnits();

    const [searchTerm, setSearchTerm] = useState('');
    const [filterActive, setFilterActive] = useState(false);
    const [openForm, setOpenForm] = useState(false);
    const [editingUnit, setEditingUnit] = useState(null);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [unitToDelete, setUnitToDelete] = useState(null);
    const [filteredUnits, setFilteredUnits] = useState([]);

    useEffect(() => {
        if (userInfo?.experiencer?.id) {
            loadUnits();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userInfo?.experiencer?.id]);

    useEffect(() => {
        filterUnits();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [units, searchTerm, filterActive]);

    const loadUnits = async () => {
        try {
            await getUnits();
        } catch (error) {
            toast.error(translate('units.toasts.errors.load_failed'));
        }
    };

    const filterUnits = () => {
        let filtered = units || [];

        // Filter by search term
        if (searchTerm) {
            filtered = filtered.filter((unit) => unit.typeId?.toLowerCase().includes(searchTerm.toLowerCase()) || unit.label?.toLowerCase().includes(searchTerm.toLowerCase()) || unit.description?.toLowerCase().includes(searchTerm.toLowerCase()));
        }

        // Filter by active status
        if (filterActive) {
            filtered = filtered.filter((unit) => unit.isActive);
        }

        setFilteredUnits(filtered);
    };

    const handleCreateUnit = () => {
        setEditingUnit(null);
        setOpenForm(true);
    };

    const handleEditUnit = (unit) => {
        setEditingUnit(unit);
        setOpenForm(true);
    };

    const handleDeleteUnit = (unit) => {
        setUnitToDelete(unit);
        setDeleteModalOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!unitToDelete) return;

        try {
            await deleteUnit(unitToDelete.id);
            toast.success(translate('units.toasts.success.deleted'));
            setDeleteModalOpen(false);
            setUnitToDelete(null);
        } catch (error) {
            toast.error(translate('units.toasts.errors.delete_failed'));
        }
    };

    const handleFormSubmit = async (unitData) => {
        try {
            if (editingUnit) {
                await updateUnit(editingUnit.id, unitData);
                toast.success(translate('units.toasts.success.updated'));
            } else {
                await createUnit(unitData);
                toast.success(translate('units.toasts.success.created'));
            }
            setOpenForm(false);
            setEditingUnit(null);
        } catch (error) {
            const errorMessage = error.message || translate('units.toasts.errors.create_failed');
            toast.error(errorMessage);
        }
    };

    const handleFormCancel = () => {
        setOpenForm(false);
        setEditingUnit(null);
    };

    const formatAgeRange = (minAge, maxAge) => {
        if (!minAge && !maxAge) return '-';
        if (!minAge) return `≤ ${maxAge}`;
        if (!maxAge) return `≥ ${minAge}`;
        return `${minAge}-${maxAge}`;
    };

    const getStatusChip = (isActive) => {
        const color = isActive ? '#50C878' : '#9EC4D6';

        return <Chip label={isActive ? translate('units.status.active') : translate('units.status.inactive')} sx={{ background: `${color}40`, color, padding: '8px 4px', borderRadius: '4px' }} />;
    };

    const prettifyUnits = (units = []) => {
        return units.map((unit) => ({
            ...unit,
            typeId: unit.typeId,
            label: unit.label || '-',
            description: unit.description || '-',
            ageRange: formatAgeRange(unit.minAge, unit.maxAge),
            status: getStatusChip(unit.isActive),
            actions: (
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title={translate('units.actions.edit')}>
                        <IconButton size="small" onClick={() => handleEditUnit(unit)} color="primary">
                            <Edit fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={translate('units.actions.delete')}>
                        <IconButton size="small" onClick={() => handleDeleteUnit(unit)} color="error">
                            <Delete fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            ),
        }));
    };

    if (!userInfo) {
        return (
            <Layout contentClassName={classes.container}>
                <Box className={classes.loadingContainer}>
                    <CircularIndeterminate centered={true} />
                </Box>
            </Layout>
        );
    }

    if (!userInfo?.experiencer?.id) {
        return (
            <Layout contentClassName={classes.container}>
                <Alert severity="error">{translate('units.toasts.errors.load_failed')}</Alert>
            </Layout>
        );
    }

    return (
        <Layout contentClassName={classes.container}>
            <Box className={classes.header}>
                <Box>
                    <Typography variant="h4" className={classes.title}>
                        {translate('units.title')}
                    </Typography>
                    <Typography variant="h6" className={classes.subtitle}>
                        {translate('units.subtitle')}
                    </Typography>
                </Box>
                <Button variant="contained" color="primary" startIcon={<Add />} onClick={handleCreateUnit} className={classes.addButton}>
                    {translate('units.add_unit')}
                </Button>
            </Box>

            <Box className={classes.filters}>
                <TextField placeholder={translate('units.search_placeholder')} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className={classes.searchField} variant="outlined" />
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Checkbox labelComponent={translate('units.filter_active')} checked={filterActive} onChange={(checked) => setFilterActive(checked)} />
                </Box>
            </Box>

            {loading ? (
                <Box className={classes.loadingContainer}>
                    <CircularIndeterminate centered={true} />
                </Box>
            ) : filteredUnits.length === 0 ? (
                <Box className={classes.emptyState}>
                    <Typography variant="h6" className={classes.emptyTitle}>
                        {translate('units.no_units')}
                    </Typography>
                    <Typography variant="body2" className={classes.emptyDescription}>
                        {translate('units.no_units_description')}
                    </Typography>
                    <Button variant="contained" color="primary" startIcon={<Add />} onClick={handleCreateUnit} className={classes.emptyButton}>
                        {translate('units.create_unit')}
                    </Button>
                </Box>
            ) : (
                <BasicTable
                    columns={[
                        { align: 'left', val: 'typeId', label: translate('units.columns.type_id') },
                        { align: 'left', val: 'label', label: translate('units.columns.label') },
                        { align: 'left', val: 'description', label: translate('units.columns.description') },
                        { align: 'center', val: 'ageRange', label: translate('units.columns.min_age') + ' - ' + translate('units.columns.max_age') },
                        { align: 'left', val: 'status', label: translate('units.columns.status') },
                        { align: 'left', val: 'actions', label: translate('units.columns.actions') },
                    ]}
                    values={prettifyUnits(filteredUnits)}
                />
            )}

            {/* Unit Form Dialog */}
            <Dialog open={openForm} dialogTitle={editingUnit ? translate('units.edit_unit') : translate('units.create_unit')} dialogContent={<UnitForm unit={editingUnit} onSubmit={handleFormSubmit} onCancel={handleFormCancel} />} handleClose={handleFormCancel} hideBack hideSubmit />

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteModalOpen} dialogTitle={translate('units.delete_modal.title')} dialogContent={translate('units.delete_modal.message')} handleClose={() => setDeleteModalOpen(false)} handleSubmit={handleConfirmDelete} submitButtonText={translate('units.delete_modal.confirm')} cancelButtonText={translate('units.delete_modal.cancel')} />
        </Layout>
    );
};

UnitsPage.displayName = 'UnitsPage';

export default UnitsPage;
